import { ChatMessage } from "./ChatMessage";
import { bylineInitials } from "@/features/auth/impersonation/authorByline";
import type { ChatMessage as ChatMessageType } from "@/features/leads/api/useInternalChat";

/** Real date + time on each message, e.g. "Today, 11:50 AM" / "Yesterday,
 *  5:05 PM" / "28 Jun, 2:30 PM" / "1 Jul 2025, 9:00 AM" (year only when not
 *  the current year). Previously showed time-only, so you couldn't tell the day. */
function formatChatTimestamp(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const now = new Date();
  const time = d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  const startOfDay = (x: Date) =>
    new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const dayDiff = Math.round((startOfDay(now) - startOfDay(d)) / 86_400_000);
  if (dayDiff === 0) return `Today, ${time}`;
  if (dayDiff === 1) return `Yesterday, ${time}`;
  const datePart = d.toLocaleDateString([], {
    day: "numeric",
    month: "short",
    ...(d.getFullYear() !== now.getFullYear() ? { year: "numeric" } : {}),
  });
  return `${datePart}, ${time}`;
}

interface ChatMessageListProps {
  messages: ChatMessageType[];
  currentUserId: number | undefined;
  isLoading: boolean;
  onReply?: (msg: ChatMessageType) => void;
  /** Delete-for-everyone — offered only on the viewer's own messages. */
  onDelete?: (msg: ChatMessageType) => void;
  /** Edit — offered only on the viewer's own live text messages. */
  onEdit?: (msg: ChatMessageType) => void;
  /** Reply-preview click → scroll to the quoted message. */
  onJumpTo?: (messageId: string) => void;
}

export function ChatMessageList({
  messages,
  currentUserId,
  isLoading,
  onReply,
  onDelete,
  onEdit,
  onJumpTo,
}: ChatMessageListProps) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12 text-[12px] text-[#9ba3a0]">
        Loading messages...
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-1">
        <p className="text-[12px] text-[#697571]">No messages yet</p>
        <p className="text-[11px] text-[#9ba3a0]">Start the conversation</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 px-6 py-2 w-full">
      {messages.map((msg) => {
        // An automation's message ("System") is nobody's own — not even the
        // rule maker's, whose account owns the row.
        const isSent = msg.senderId === currentUserId && !msg.isSystem;
        const time = formatChatTimestamp(msg.createdAt);
        // "Switch user": a message typed by a manager viewing as this account
        // always names them — to everyone, including the account's owner, who
        // would otherwise read it as something they sent themselves.
        // `isSent` is untouched: it still means "this came from my account"
        // and keeps driving alignment, colour, read receipts and edit/delete.
        const switchedName = msg.switchedByUser?.name ?? null;

        return (
          <ChatMessage
            key={msg.id}
            message={{
              id: msg.id,
              text: msg.body || "",
              time,
              isDeleted: msg.type === "deleted",
              isEdited: Boolean(msg.editedAt),
              author: switchedName ?? (isSent ? undefined : msg.sender?.name),
              authorVia: switchedName ? msg.sender?.name : undefined,
              role: undefined,
              isSent,
              avatar: isSent
                ? undefined
                : bylineInitials(
                    msg.sender?.name,
                    switchedName ? { name: switchedName } : null,
                  ),
              file:
                msg.mediaUrl && msg.type !== "text"
                  ? { name: msg.mediaName || "File", type: msg.mediaMime || "" }
                  : undefined,
              mediaUrl: msg.mediaUrl || undefined,
              readBy: msg.readBy,
              mentions: msg.mentions as any,
              replyTo: msg.replyTo || undefined,
            }}
            onReply={onReply ? () => onReply(msg) : undefined}
            onDelete={
              onDelete && isSent && msg.type !== "deleted"
                ? () => onDelete(msg)
                : undefined
            }
            onEdit={
              onEdit && isSent && msg.type === "text" && !msg.mediaUrl
                ? () => onEdit(msg)
                : undefined
            }
            onJumpTo={onJumpTo}
          />
        );
      })}
    </div>
  );
}
